/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { apiKeys, GCP_CONFIG } from '@shared/config';
import { getGWorkspaceAccessToken } from './tokenService';
import { Logger } from './logger';

/**
 * Service for interacting with Google Cloud Platform services via REST APIs.
 * Supports:
 * - Google Cloud Storage (JSON API)
 * - Cloud Firestore (REST API)
 */

const hasValidGCPKeys = () => {
    const workspace = apiKeys.googleWorkspace;
    const apiKey = apiKeys.googleApiKey;
    
    if (!workspace || !apiKey) return false;
    if (workspace.refreshToken.toUpperCase().includes('PLACEHOLDER')) return false;
    if (apiKey.toUpperCase().includes('PLACEHOLDER')) return false;
    
    return true;
};

export const uploadFileToStorage = async (file: File, path: string): Promise<string> => {
    if (!hasValidGCPKeys()) {
        Logger.info(`[MOCK MODE] GCP Storage: Would upload ${file.name} to ${path}. Missing or placeholder GCP keys.`);
        return URL.createObjectURL(file); // Return local blob URL for testing
    }

    const bucket = GCP_CONFIG.bucketName;
    const objectName = encodeURIComponent(path);
    const url = `https://storage.googleapis.com/upload/storage/v1/b/${bucket}/o?uploadType=media&name=${objectName}&key=${apiKeys.googleApiKey}`;

    const accessToken = await getGWorkspaceAccessToken();
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': file.type,
            },
            body: file,
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(`GCP Storage Upload Failed: ${error.error?.message || response.statusText}`);
        }

        const result = await response.json();
        // Return public URL or authorized URL depending on bucket policy. 
        // Assuming public readable for assets or using authenticated links.
        return `https://storage.googleapis.com/${bucket}/${path}`;
    } catch (error) {
        Logger.error('Failed to upload file to GCP Storage', error);
        throw error;
    }
};

export const saveDocumentToFirestore = async (collection: string, docId: string, data: any): Promise<void> => {
    if (!hasValidGCPKeys()) {
        Logger.info(`[MOCK MODE] Firestore: Would save document ${docId} to ${collection}. Placeholder or missing keys.`, data);
        return;
    }

    const projectId = GCP_CONFIG.projectId;
    const url = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/${collection}/${docId}?key=${apiKeys.googleApiKey}`;

    // Firestore REST API requires a specific JSON structure for values
    const firestoreData = toFirestoreValue(data);

    const accessToken = await getGWorkspaceAccessToken();
    try {
        const response = await fetch(url, {
            method: 'PATCH', // Helper to create or update
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ fields: firestoreData.mapValue.fields }),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(`Firestore Save Failed: ${error.error?.message || response.statusText}`);
        }

        Logger.info(`Document ${docId} saved to Firestore collection ${collection}`);
    } catch (error) {
        Logger.error('Failed to save document to Firestore', error);
        throw error;
    }
};

export const getDocumentFromFirestore = async (collection: string, docId: string): Promise<any> => {
    if (!hasValidGCPKeys()) {
        Logger.info(`[MOCK MODE] Firestore: Would fetch document ${docId} from ${collection}. Placeholder or missing keys.`);
        return null;
    }

    const projectId = GCP_CONFIG.projectId;
    const url = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/${collection}/${docId}?key=${apiKeys.googleApiKey}`;

    const accessToken = await getGWorkspaceAccessToken();
    try {
        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${accessToken}`
            }
        });

        if (!response.ok) {
            if (response.status === 404) return null;
            const error = await response.json();
            throw new Error(`Firestore Fetch Failed: ${error.error?.message || response.statusText}`);
        }

        const result: any = await response.json();
        return fromFirestoreValue(result.fields);
    } catch (error) {
        Logger.error('Failed to fetch document from Firestore', error);
        throw error;
    }
};

// Helper to convert JS object to Firestore Value format
// Simplified implementation for common types
const toFirestoreValue = (val: any): any => {
    if (val === null) return { nullValue: null };
    if (typeof val === 'boolean') return { booleanValue: val };
    if (typeof val === 'number') {
        if (Number.isInteger(val)) return { integerValue: val };
        return { doubleValue: val };
    }
    if (typeof val === 'string') return { stringValue: val };
    if (Array.isArray(val)) {
        return { arrayValue: { values: val.map(toFirestoreValue) } };
    }
    if (typeof val === 'object') {
        const fields: any = {};
        for (const k in val) {
            fields[k] = toFirestoreValue(val[k]);
        }
        return { mapValue: { fields } };
    }
    return { stringValue: String(val) }; // Fallback
};

// Helper to convert Firestore Value format back to JS object
const fromFirestoreValue = (fields: any): any => {
    const obj: any = {};
    for (const key in fields) {
        const val = fields[key];
        if (val.stringValue) obj[key] = val.stringValue;
        else if (val.booleanValue) obj[key] = val.booleanValue;
        else if (val.integerValue) obj[key] = parseInt(val.integerValue);
        else if (val.doubleValue) obj[key] = parseFloat(val.doubleValue);
        else if (val.arrayValue) obj[key] = (val.arrayValue.values || []).map((v: any) => fromSingleValue(v));
        else if (val.mapValue) obj[key] = fromFirestoreValue(val.mapValue.fields);
        else if (val.nullValue) obj[key] = null;
    }
    return obj;
};

const fromSingleValue = (val: any): any => {
    if (val.stringValue) return val.stringValue;
    if (val.booleanValue) return val.booleanValue;
    if (val.integerValue) return parseInt(val.integerValue);
    if (val.doubleValue) return parseFloat(val.doubleValue);
    if (val.mapValue) return fromFirestoreValue(val.mapValue.fields);
    return null;
};
